import { LRUCache } from "lru-cache";
import { useEffect } from "react";

// Caches the *signed URL string* of an image, keyed by a stable id (frame
// image_id / product_id), and reuses it across tasks. Presigned GCS image URLs
// are regenerated on every fetch, so without it the browser's HTTP cache misses
// across tasks even for identical images. An identical URL lets the browser
// serve the bytes from its own cache (or a cheap 304), whether an <img> or the
// frame viewer's fetch() loads it. Each entry is dropped before its signature
// expires so we never request a dead URL — expiry is read from the GCS signing
// params, with a safety margin.

type ImageCache = LRUCache<string, string>;

// Stop reusing a signed URL this long before it actually expires. Only needs to
// cover client/GCS clock skew + the brief gap before the browser fires the
// request (once fetched, the bytes are cached and expiry no longer matters).
const EXPIRY_MARGIN_MS = 60 * 1000;
// Fallback lifetime when a URL carries no recognizable expiry param.
const DEFAULT_TTL_MS = 5 * 60 * 1000;

function makeCache(max: number): ImageCache {
  // ponytail: count-bounded LRU + per-entry TTL, purged on access (no timers).
  // If memory/precision ever matters, tune max or switch to maxSize.
  return new LRUCache<string, string>({ max, ttl: DEFAULT_TTL_MS });
}

// Separate instances so heavy frame scrubbing can't evict the product grid.
export const frameImageCache = makeCache(1000);
export const productImageCache = makeCache(2000);

// Absolute expiry (ms epoch) from a GCS v4 signed URL, or null if not present.
function gcsUrlExpiry(url: string): number | null {
  let params: URLSearchParams;
  try {
    params = new URL(url).searchParams;
  } catch {
    return null;
  }

  const date = params.get("X-Goog-Date"); // basic-ISO, e.g. 20260724T124522Z
  const expires = Number(params.get("X-Goog-Expires")); // seconds
  if (!date || !Number.isFinite(expires) || expires <= 0) return null;

  const iso = date.replace(
    /^(\d{4})(\d{2})(\d{2})T(\d{2})(\d{2})(\d{2})Z$/,
    "$1-$2-$3T$4:$5:$6Z",
  );
  const start = Date.parse(iso);
  return Number.isNaN(start) ? null : start + expires * 1000;
}

// Stores `srcUrl` for `id` until shortly before its signature expires. On a hit
// we keep the stored URL (a reused URL the browser can serve from its own
// cache); only compute/store on a miss.
function rememberImageUrl(cache: ImageCache, id: string, srcUrl: string) {
  if (cache.has(id)) return;
  const expiry = gcsUrlExpiry(srcUrl);
  const ttl = expiry ? expiry - Date.now() - EXPIRY_MARGIN_MS : DEFAULT_TTL_MS;
  if (ttl > 0) cache.set(id, srcUrl, { ttl });
}

/**
 * Returns a still-valid signed URL for `id`, reusing the previously-issued one
 * (so the browser serves the bytes from its HTTP cache) until it nears expiry,
 * then falling through to the fresh `srcUrl`. Synchronous — always returns a
 * usable URL, or undefined when there's nothing to show yet.
 */
export function useCachedImageUrl(
  cache: ImageCache,
  id: string | undefined,
  srcUrl: string | undefined,
): string | undefined {
  // get() respects TTL (returns undefined once expired) and bumps recency.
  const cached = id ? cache.get(id) : undefined;

  useEffect(() => {
    if (!id || !srcUrl) return;
    rememberImageUrl(cache, id, srcUrl);
  }, [cache, id, srcUrl]);

  return cached ?? srcUrl ?? undefined;
}

/**
 * The same as useCachedImageUrl, outside React: for code that loads images
 * itself, e.g. the frame cache, which fetches frames to decode them.
 */
export function resolveCachedImageUrl(
  cache: ImageCache,
  id: string,
  srcUrl: string,
): string {
  const cached = cache.get(id);
  if (cached) return cached;
  rememberImageUrl(cache, id, srcUrl);
  return srcUrl;
}
